import React, { useState } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
} from 'src/components/ui';
import { FaRobot, FaPenAlt, FaMagic, FaShoppingBag, FaArrowLeft } from 'react-icons/fa';
import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import { useOnboarding } from 'src/contexts/onboarding';
import useCopilotChatContext from 'src/fusion-copilot/hooks/useCopilotChatContext';
import { useRealtimeChatContext } from 'src/fusion-copilot/hooks/useRealtimeChatContext';
import { useRightSidebarContext } from 'src/contexts/app/RightSidebarContext';
import {
  RIGHT_SIDEBAR_WIDTH_COLLAPSED,
  TOP_BAR_HEIGHT,
  IMAGE_PLACEHOLDER,
} from 'src/components/campaigns/utils/constants';
import { ICatalog } from 'src/lib/schemas';

interface CampaignWizardModalProps {
  isOpen: boolean;
  onClose: (showStepsOverlay?: boolean) => void;
  products?: ICatalog[];
  onProductSelect?: (product: ICatalog) => void;
}

/**
 * CampaignWizardModal Component
 *
 * Shows on first visit to /projects/campaigns/new
 * Offers two paths: manual form fill or AI assistant help
 *
 * Redesigned to match WelcomeWizard style with gradient header and smooth animations
 */
const CampaignWizardModal: React.FC<CampaignWizardModalProps> = ({
  isOpen,
  onClose,
  products = [],
  onProductSelect,
}) => {
  const { setWizard2Shown, triggerCampaignFormTour } = useOnboarding();
  const { openCopilot } = useCopilotChatContext();
  const { sendAutoMessage } = useRealtimeChatContext();
  const { handleOpenSidebar } = useRightSidebarContext();

  // State for product selection flow
  const [view, setView] = useState<'options' | 'products'>('options');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<ICatalog | null>(null);

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasProducts = products.length > 0;

  const handleFillMyself = () => {
    setWizard2Shown();
    // Close modal and start the campaign form guided tour
    onClose(false);
    // Small delay to let modal close before starting tour
    setTimeout(() => {
      triggerCampaignFormTour();
    }, 100);
  };

  const handleAIHelp = async () => {
    setWizard2Shown();
    // Don't show steps overlay for AI help
    onClose(false);

    // Open the copilot sidebar directly
    handleOpenSidebar('copilot');

    // Small delay to ensure copilot is open, then send the auto message
    setTimeout(async () => {
      openCopilot();
      // Send "Get more leads" as if user typed it
      if (sendAutoMessage) {
        await sendAutoMessage('Get more leads');
      }
    }, 500);
  };

  const handleStartWithProduct = () => {
    setView('products');
  };

  const handleBackToOptions = () => {
    setView('options');
    setSelectedProduct(null);
    setSearchTerm('');
  };

  const handleProductConfirm = () => {
    if (selectedProduct && onProductSelect) {
      setWizard2Shown();
      onProductSelect(selectedProduct);
      onClose(false);
      // Reset state
      setView('options');
      setSelectedProduct(null);
      setSearchTerm('');
    }
  };

  const handleClose = () => {
    setWizard2Shown();
    // Don't show overlay when closing with X button
    onClose(false);
    // Reset state
    setView('options');
    setSelectedProduct(null);
    setSearchTerm('');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - starts below TopBar, stops before right sidebar to avoid double overlay */}
      <Box
        position="fixed"
        top={TOP_BAR_HEIGHT}
        left={0}
        right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        bottom={0}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        zIndex={10000}
        onClick={handleClose}
        sx={{
          animation: 'fadeIn 0.2s ease-out',
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 },
          },
        }}
      />

      {/* Dark overlay for TopBar - same style as rest of overlay */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        h={TOP_BAR_HEIGHT}
        zIndex={10202}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        pointerEvents="none"
      />

      {/* Dark overlay for Right Sidebar - covers entire sidebar height */}
      <Box
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        zIndex={10202}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        pointerEvents="none"
      />

      {/* Modal */}
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        zIndex={10001}
        w="90%"
        maxW="480px"
        bg="white"
        borderRadius="24px"
        boxShadow="0 25px 60px -15px rgba(0, 0, 0, 0.3)"
        overflow="hidden"
        sx={{
          animation: 'modalEnter 0.3s ease-out',
          '@keyframes modalEnter': {
            '0%': {
              opacity: 0,
              transform: 'translate(-50%, -48%) scale(0.95)',
            },
            '100%': {
              opacity: 1,
              transform: 'translate(-50%, -50%) scale(1)',
            },
          },
        }}
      >
        {/* Header with gradient and decorative elements */}
        <Box
          bg="linear-gradient(135deg, #F6851E 0%, #FF9F4A 50%, #F6851E 100%)"
          pt={8}
          pb={10}
          px={8}
          position="relative"
          overflow="hidden"
        >
          {/* Decorative circles */}
          <Box
            position="absolute"
            top="-30px"
            right="-30px"
            w="100px"
            h="100px"
            borderRadius="full"
            bg="rgba(255,255,255,0.1)"
          />
          <Box
            position="absolute"
            bottom="-40px"
            left="-20px"
            w="120px"
            h="120px"
            borderRadius="full"
            bg="rgba(255,255,255,0.08)"
          />
          <Box
            position="absolute"
            top="30px"
            left="25%"
            w="50px"
            h="50px"
            borderRadius="full"
            bg="rgba(255,255,255,0.05)"
          />

          {/* Close button */}
          <IconButton
            aria-label="Close"
            icon={<CloseIcon boxSize={3} />}
            position="absolute"
            top={4}
            right={4}
            size="sm"
            variant="ghost"
            color="white"
            _hover={{ bg: 'rgba(255,255,255,0.2)' }}
            onClick={handleClose}
            zIndex={1}
          />

          {/* Header content */}
          <VStack spacing={3} position="relative" zIndex={1}>
            {/* Icon */}
            <Box
              w="56px"
              h="56px"
              bg="rgba(255,255,255,0.2)"
              borderRadius="16px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                animation: 'iconFloat 3s ease-in-out infinite',
                '@keyframes iconFloat': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-4px)' },
                },
              }}
            >
              <Icon as={view === 'products' ? FaShoppingBag : FaMagic} color="white" boxSize={6} />
            </Box>
            <VStack spacing={1}>
              <Text fontSize="xl" fontWeight="bold" color="white" letterSpacing="-0.5px">
                {view === 'products' ? 'What would you like to promote?' : "Let's create your campaign!"}
              </Text>
              <Text fontSize="sm" color="whiteAlpha.900" fontWeight="500">
                {view === 'products' ? 'Select a product or service' : "Choose how you'd like to proceed"}
              </Text>
            </VStack>
          </VStack>
        </Box>

        {/* Content */}
        <VStack spacing={5} py={6} px={6}>
          {view === 'options' ? (
            <>
              {/* Options */}
              <VStack spacing={3} w="full">
                {/* AI Assistant option - Recommended */}
                <Box
                  as="button"
                  w="full"
                  p={5}
                  bg="orange.50"
                  border="2px solid"
                  borderColor="orange.400"
                  borderRadius="16px"
                  _hover={{
                    bg: 'orange.100',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(246, 133, 30, 0.15)',
                  }}
                  _active={{ transform: 'translateY(0)' }}
                  transition="all 0.2s"
                  onClick={handleAIHelp}
                  textAlign="left"
                  sx={{
                    animation: 'slideInLeft 0.4s ease-out 0.1s both',
                    '@keyframes slideInLeft': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateX(-20px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                  }}
                >
                  <HStack spacing={4}>
                    <Box
                      p={3}
                      bg="white"
                      borderRadius="12px"
                      boxShadow="0 2px 8px rgba(246, 133, 30, 0.15)"
                    >
                      <Icon as={FaRobot} boxSize={5} color="orange.500" />
                    </Box>
                    <VStack align="start" spacing={1} flex={1}>
                      <HStack spacing={2}>
                        <Text fontWeight="600" color="gray.800" fontSize="md">
                          Help me with AI
                        </Text>
                        <Text
                          fontSize="xs"
                          bg="orange.500"
                          color="white"
                          px={2}
                          py={0.5}
                          borderRadius="full"
                          fontWeight="600"
                        >
                          Recommended
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        Let our AI guide you through the process
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                {/* Manual option */}
                <Box
                  as="button"
                  w="full"
                  p={5}
                  bg="white"
                  border="2px solid"
                  borderColor="gray.200"
                  borderRadius="16px"
                  _hover={{
                    borderColor: 'gray.300',
                    bg: 'gray.50',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
                  }}
                  _active={{ transform: 'translateY(0)' }}
                  transition="all 0.2s"
                  onClick={handleFillMyself}
                  textAlign="left"
                  sx={{
                    animation: 'slideInLeft 0.4s ease-out 0.2s both',
                  }}
                >
                  <HStack spacing={4}>
                    <Box
                      p={3}
                      bg="gray.100"
                      borderRadius="12px"
                    >
                      <Icon as={FaPenAlt} boxSize={5} color="gray.600" />
                    </Box>
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontWeight="600" color="gray.800" fontSize="md">
                        I'll fill the form myself
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Complete the campaign details step by step
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                {/* Start with a product option - only show if products exist */}
                {hasProducts && (
                  <Box
                    as="button"
                    w="full"
                    p={5}
                    bg="white"
                    border="2px solid"
                    borderColor="gray.200"
                    borderRadius="16px"
                    _hover={{
                      borderColor: 'purple.300',
                      bg: 'purple.50',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(128, 90, 213, 0.1)',
                    }}
                    _active={{ transform: 'translateY(0)' }}
                    transition="all 0.2s"
                    onClick={handleStartWithProduct}
                    textAlign="left"
                    sx={{
                      animation: 'slideInLeft 0.4s ease-out 0.3s both',
                    }}
                  >
                    <HStack spacing={4}>
                      <Box
                        p={3}
                        bg="purple.100"
                        borderRadius="12px"
                      >
                        <Icon as={FaShoppingBag} boxSize={5} color="purple.600" />
                      </Box>
                      <VStack align="start" spacing={1} flex={1}>
                        <HStack spacing={2}>
                          <Text fontWeight="600" color="gray.800" fontSize="md">
                            Start with a product
                          </Text>
                          <Badge colorScheme="purple" fontSize="xs" borderRadius="full">
                            {products.length}
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color="gray.500">
                          Select a product or service to promote
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                )}
              </VStack>

              {/* Skip link */}
              <Button
                variant="ghost"
                color="gray.400"
                size="sm"
                fontWeight="500"
                _hover={{ color: 'gray.600', bg: 'transparent' }}
                onClick={handleClose}
                sx={{
                  animation: 'fadeIn 0.3s ease-out 0.3s both',
                }}
              >
                Skip for now
              </Button>
            </>
          ) : (
            <>
              {/* Product Selection View */}
              <VStack spacing={4} w="full">
                {/* Back button */}
                <HStack w="full" justify="flex-start">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Icon as={FaArrowLeft} />}
                    color="gray.500"
                    _hover={{ color: 'gray.700', bg: 'gray.100' }}
                    onClick={handleBackToOptions}
                  >
                    Back
                  </Button>
                </HStack>

                {/* Search */}
                {products.length > 5 && (
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" boxSize={3} />
                    </InputLeftElement>
                    <Input
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      borderRadius="full"
                      fontSize="sm"
                      bg="gray.50"
                      border="none"
                      _focus={{ bg: 'white', boxShadow: 'sm' }}
                    />
                  </InputGroup>
                )}

                {/* Product list */}
                <VStack
                  spacing={2}
                  align="stretch"
                  w="full"
                  maxH="280px"
                  overflowY="auto"
                  pr={1}
                >
                  {filteredProducts.length === 0 ? (
                    <Text color="gray.500" textAlign="center" py={4}>
                      No products found
                    </Text>
                  ) : (
                    filteredProducts.map((product) => {
                      const productId = product.id || (product as any)._id;
                      const selectedId = selectedProduct?.id || (selectedProduct as any)?._id;
                      const isSelected = selectedProduct !== null && productId === selectedId;

                      return (
                        <Box
                          key={productId}
                          p={3}
                          borderWidth="2px"
                          borderColor={isSelected ? 'orange.400' : 'gray.100'}
                          borderRadius="xl"
                          cursor="pointer"
                          bg={isSelected ? 'orange.50' : 'white'}
                          _hover={{
                            borderColor: isSelected ? 'orange.400' : 'gray.200',
                            bg: isSelected ? 'orange.50' : 'gray.50',
                          }}
                          transition="all 0.2s"
                          onClick={() => setSelectedProduct(product)}
                        >
                          <HStack spacing={3}>
                            <Box
                              bg="gray.100"
                              borderRadius="lg"
                              p={1.5}
                              flexShrink={0}
                            >
                              <Image
                                src={product.image || (product as any).details?.image}
                                fallbackSrc={IMAGE_PLACEHOLDER}
                                alt={product.name}
                                boxSize="40px"
                                borderRadius="md"
                                objectFit="cover"
                              />
                            </Box>
                            <VStack align="start" spacing={0} flex={1} minW={0}>
                              <Text
                                fontWeight="semibold"
                                fontSize="sm"
                                noOfLines={1}
                                color="gray.800"
                              >
                                {product.name}
                              </Text>
                              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                {product.url ? new URL(product.url).hostname : product.kind || 'Product'}
                              </Text>
                            </VStack>
                            <Box
                              w="18px"
                              h="18px"
                              borderRadius="full"
                              borderWidth="2px"
                              borderColor={isSelected ? 'orange.400' : 'gray.200'}
                              bg={isSelected ? 'orange.400' : 'white'}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              {isSelected && (
                                <Box w="6px" h="6px" borderRadius="full" bg="white" />
                              )}
                            </Box>
                          </HStack>
                        </Box>
                      );
                    })
                  )}
                </VStack>

                {/* Confirm button */}
                <Button
                  w="full"
                  colorScheme="orange"
                  isDisabled={!selectedProduct}
                  onClick={handleProductConfirm}
                  size="md"
                  borderRadius="full"
                >
                  Continue with this product
                </Button>
              </VStack>
            </>
          )}
        </VStack>
      </Box>
    </>
  );
};

export default CampaignWizardModal;
